import { ClientResponseError } from "pocketbase";
import { RequestSource } from "@/lib/errorHandling";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export function useHandleError() {
  const { t } = useTranslation(["error"]);

  const PB_MESSAGE_TO_I18N_KEY = new Map([
    ["Failed to authenticate.", "pbFailedToAuthenticate"],
    [
      "Something went wrong while processing your request.",
      "pbSomethingWentWrong",
    ],
    // Add more mappings as needed
  ]);

  const parsePocketbaseError = useCallback(
    (source: RequestSource, error: ClientResponseError): string => {
      let action = "";
      let details = "";

      const originalErrorMsg = (error.originalError as Error)?.message || "";

      const messageKey =
        PB_MESSAGE_TO_I18N_KEY.get(error.message) || "pbSomethingWentWrong";

      let message = messageKey;

      // Extract code fields from error.data
      const codes: string[] = [];
      if (error.data && typeof error.data === "object") {
        Object.values(error.data).forEach((value) => {
          if (value && typeof value === "object") {
            Object.values(value).forEach((nestedValue) => {
              if (
                nestedValue &&
                typeof nestedValue === "object" &&
                "code" in nestedValue
              ) {
                let codeValue = nestedValue.code as string;
                if (source === RequestSource.SIGNUP) {
                  codeValue = `${codeValue}_signup`;
                }
                codes.push(codeValue);
              }
            });
          }
        });
      }

      if (messageKey === "pbFailedToAuthenticate") {
        action = "checkCredentialsTryAgain";
      }
      if (messageKey === "pbSomethingWentWrong") {
        if (
          (source === RequestSource.SIGNUP ||
            source === RequestSource.CHANGE_PASSWORD) &&
          codes.length > 0
        ) {
          // detail from code contains all required info
          message = "";
          action = "";
        } else {
          // For other sources, suggest trying again later
          action = "tryAgainLater";
        }
      }

      // Expand details messages based on codes
      if (codes.length > 0) {
        details = codes
          .map((code) => t(`error:externalErrorDetails.${code}`))
          .join("\n");
      }

      // Build final message
      let finalMessage = "";
      if (message !== "") {
        finalMessage += t(`error:externalErrorMessages.${message}`);
      }
      if (action !== "") {
        finalMessage += " " + t(`error:errorUserActions.${action}`);
      }
      if (details !== "") {
        finalMessage += " " + details;
      }
      if (originalErrorMsg) {
        finalMessage += ` (${originalErrorMsg})`;
      }

      return finalMessage;
    },
    [t],
  );

  const handleError = (source: RequestSource, error: unknown) => {
    if (error instanceof ClientResponseError) {
      console.log("ClientResponseError:", error);
      return parsePocketbaseError(source, error);
    } else if (error instanceof Error) {
      console.log("Other error:", error);
      return `${error.message}`;
    } else {
      return "An unknown error occurred:" + JSON.stringify(error);
    }
  };

  return { handleError };
}
